import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Flame, LayoutDashboard, Package, Layers, Trophy, Inbox, LogOut, Plus, Pencil, Trash2,
  Mail, Phone, ArrowLeft, Search, Settings as SettingsIcon, Film, Save, Building2, Briefcase, MapPin, X, Users, Award, Link as LinkIcon,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import ImageUpload from '../../components/admin/ImageUpload';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import api, { apiErrorMessage, mediaUrl, asArray } from '../../lib/api';
import { useSite } from '../../context/SiteContext';
import { PRODUCT_RANGE } from '../../mock';

// Best-effort delete of a file the CMS previously uploaded. No-op for pasted
// external URLs or the seeded catalog images — only our own uploads are removed.
const FILES_BASE = '/api/files/';
const UPLOAD_MARKER = '/api/files/closefire/uploads/';
async function deleteUploadedFile(url) {
  if (typeof url !== 'string') return;
  const at = url.indexOf(UPLOAD_MARKER);
  if (at === -1) return;
  const rel = url.slice(at + FILES_BASE.length); // -> closefire/uploads/<name>
  try { await api.delete(`/files/${rel}`); } catch (e) { /* orphan cleanup is non-critical */ }
}

const NAV = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'products', label: 'Products', icon: Package },
  { key: 'solutions', label: 'Solutions', icon: Layers },
  { key: 'stories', label: 'Success Stories', icon: Trophy },
  { key: 'clients', label: 'Clients', icon: Users },
  { key: 'certifications', label: 'Certifications', icon: Award },
  { key: 'submissions', label: 'Submissions', icon: Inbox },
  { key: 'settings', label: 'Hero & Settings', icon: SettingsIcon },
];

const CATEGORIES = ['Fire Extinguisher', 'Fire Hydrant System', 'Fire Alarm System'];

const FIELDS = {
  products: [
    { name: 'name', label: 'Product name', type: 'text' },
    { name: 'category', label: 'Category', type: 'select', options: CATEGORIES },
    { name: 'desc', label: 'Short description', type: 'textarea' },
    { name: 'long', label: 'Long description', type: 'textarea' },
    { name: 'image', label: 'Primary image', type: 'image' },
  ],
  solutions: [
    { name: 'title', label: 'Solution title', type: 'text' },
    { name: 'image', label: 'Image', type: 'image' },
  ],
  stories: [
    { name: 'title', label: 'Story title', type: 'text' },
    { name: 'location', label: 'Location', type: 'text' },
    { name: 'excerpt', label: 'Excerpt', type: 'textarea' },
    { name: 'image', label: 'Image', type: 'image' },
  ],
  clients: [
    { name: 'name', label: 'Client / company name', type: 'text' },
    { name: 'logo', label: 'Logo', type: 'image' },
  ],
  certifications: [
    { name: 'name', label: 'Certification name (e.g. ISO 9001)', type: 'text' },
    { name: 'logo', label: 'Certification logo / badge', type: 'image' },
  ],
};

function VariantEditor({ variants, onChange }) {
  const list = variants || [];
  const setV = (i, patch) => onChange(list.map((v, idx) => (idx === i ? { ...v, ...patch } : v)));
  const addVariant = () => onChange([...list, { label: '', pairs: [{ k: '', val: '' }] }]);
  const removeVariant = (i) => onChange(list.filter((_, idx) => idx !== i));
  const setPair = (vi, pi, patch) => setV(vi, { pairs: list[vi].pairs.map((p, idx) => (idx === pi ? { ...p, ...patch } : p)) });
  const addPair = (vi) => setV(vi, { pairs: [...(list[vi].pairs || []), { k: '', val: '' }] });
  const removePair = (vi, pi) => setV(vi, { pairs: list[vi].pairs.filter((_, idx) => idx !== pi) });

  return (
    <div className="space-y-3" data-testid="variant-editor">
      {list.map((v, vi) => (
        <div key={vi} className="rounded-xl border border-border p-3 bg-secondary/40">
          <div className="flex items-center gap-2 mb-2">
            <Input value={v.label} onChange={(e) => setV(vi, { label: e.target.value })} placeholder="SKU / variant label (e.g. 6 Kg)" className="h-9" data-testid={`variant-label-${vi}`} />
            <Button type="button" variant="ghost" size="icon" onClick={() => removeVariant(vi)} className="text-destructive shrink-0"><Trash2 className="w-4 h-4" /></Button>
          </div>
          <div className="space-y-1.5">
            {(v.pairs || []).map((p, pi) => (
              <div key={pi} className="flex items-center gap-2">
                <Input value={p.k} onChange={(e) => setPair(vi, pi, { k: e.target.value })} placeholder="Spec name" className="h-8 text-xs" data-testid={`variant-${vi}-spec-key-${pi}`} />
                <Input value={p.val} onChange={(e) => setPair(vi, pi, { val: e.target.value })} placeholder="Value" className="h-8 text-xs" data-testid={`variant-${vi}-spec-val-${pi}`} />
                <button type="button" onClick={() => removePair(vi, pi)} className="text-muted-foreground hover:text-destructive shrink-0"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            <button type="button" onClick={() => addPair(vi)} data-testid={`add-spec-row-${vi}`} className="text-xs text-primary hover:underline">+ Add spec row</button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addVariant} className="gap-1.5" data-testid="add-variant-btn"><Plus className="w-3.5 h-3.5" /> Add variant / SKU</Button>
    </div>
  );
}

// Gallery editor for a product's extra images. The primary image is edited by
// the standard field above; this manages product.images[] where each slot can
// be changed or removed on its own.
function ProductImagesEditor({ images, onChange }) {
  const list = asArray(images).filter((s) => typeof s === 'string' && s);
  const setAt = (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x)));
  const removeAt = (i) => { deleteUploadedFile(list[i]); onChange(list.filter((_, idx) => idx !== i)); };
  const add = (v) => { if (v) onChange([...list, v]); };

  return (
    <div className="space-y-3" data-testid="product-images-editor">
      {list.length === 0 && (
        <p className="text-xs text-muted-foreground">No additional images yet — the primary image is always shown first.</p>
      )}
      {list.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {list.map((img, i) => (
            <div key={i} data-testid={`product-image-slot-${i}`}>
              <ImageUpload compact value={img} onChange={(v) => setAt(i, v)} onRemove={() => removeAt(i)} />
            </div>
          ))}
        </div>
      )}
      <div className="max-w-[calc(50%-0.375rem)]">
        <ImageUpload compact value="" onChange={add} hint="Add another image" />
      </div>
    </div>
  );
}

function EntityManager({ entity, title, singular }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    api.get(`/${entity}`).then((r) => setItems(asArray(r.data))).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entity]);

  const toPairs = (item) => ({
    ...item,
    variants: (item.variants || []).map((v) => ({ label: v.label, pairs: Object.entries(v.specs || {}).map(([k, val]) => ({ k, val: String(val) })) })),
  });
  const openNew = () => { setForm(entity === 'products' ? { category: CATEGORIES[0], featured: false, fireClasses: [], variants: [], images: [] } : {}); setEditing('new'); };
  const openEdit = (item) => { setForm(entity === 'products' ? toPairs(item) : item); setEditing(item.id); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (entity === 'products') {
        payload.variants = (form.variants || []).map((v) => ({
          label: v.label || 'Standard',
          specs: Object.fromEntries((v.pairs || []).filter((p) => p.k).map((p) => [p.k, p.val])),
        }));
        if (payload.variants.length === 0) payload.variants = [{ label: 'Standard', specs: { TYPE: payload.category || 'Product' } }];
        payload.fireClasses = payload.fireClasses || [];
        payload.featured = !!payload.featured;
        payload.images = asArray(form.images).filter((s) => typeof s === 'string' && s.trim());
      }
      if (editing === 'new') await api.post(`/cms/${entity}`, payload);
      else await api.put(`/cms/${entity}/${editing}`, payload);
      setEditing(null);
      toast({ title: `${singular} saved` });
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: apiErrorMessage(err), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/cms/${entity}/${toDelete.id}`);
      toast({ title: `${singular} deleted` });
      setToDelete(null);
      load();
    } catch (err) {
      toast({ title: 'Delete failed', description: apiErrorMessage(err), variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display font-extrabold text-3xl text-white">{title}</h2>
          <p className="text-muted-foreground">{items.length} {items.length === 1 ? 'item' : 'items'}</p>
        </div>
        <Button onClick={openNew} data-testid={`add-${entity}-btn`} className="gap-2"><Plus className="w-4 h-4" /> Add {singular}</Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground py-12 text-center">Loading…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item) => (
            <div key={item.id} className="card-dark rounded-2xl overflow-hidden card-hover">
              {(item.image || item.logo) && <div className="h-40 bg-secondary grid place-items-center"><img src={mediaUrl(item.image || item.logo)} alt="" className={item.logo && !item.image ? 'max-h-28 max-w-[70%] object-contain' : 'w-full h-full object-cover'} /></div>}
              <div className="p-5">
                {item.category && <span className="text-xs font-semibold text-primary uppercase">{item.category}</span>}
                {item.featured && <span className="ml-2 text-[10px] font-bold uppercase bg-primary/15 text-primary rounded px-1.5 py-0.5">Featured</span>}
                <h3 className="font-heading font-bold text-lg text-white mt-1 line-clamp-2">{item.name || item.title}</h3>
                {(item.desc || item.excerpt) && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{item.desc || item.excerpt}</p>}
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" data-testid={`edit-${entity}-${item.id}`} onClick={() => openEdit(item)} className="gap-1.5"><Pencil className="w-3.5 h-3.5" /> Edit</Button>
                  <Button variant="ghost" size="sm" data-testid={`delete-${entity}-${item.id}`} onClick={() => setToDelete(item)} className="gap-1.5 text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /> Delete</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing === 'new' ? `Add ${singular}` : `Edit ${singular}`}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {FIELDS[entity].map((f) => (
              <div key={f.name}>
                <Label className="mb-1.5 block">{f.label}</Label>
                {f.type === 'textarea' ? (
                  <Textarea value={form[f.name] || ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} rows={f.name === 'long' ? 4 : 3} className="resize-none" />
                ) : f.type === 'select' ? (
                  <select value={form[f.name] || f.options[0]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="h-11 w-full rounded-md bg-secondary border border-border px-3 text-sm">
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.type === 'image' ? (
                  <ImageUpload
                    value={form[f.name] || ''}
                    onChange={(v) => setForm({ ...form, [f.name]: v })}
                    onRemove={() => { deleteUploadedFile(form[f.name]); setForm({ ...form, [f.name]: '' }); }}
                  />
                ) : (
                  <Input value={form[f.name] || ''} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="h-11" required />
                )}
              </div>
            ))}

            {entity === 'products' && (
              <>
                <div className="flex items-center justify-between rounded-xl border border-border p-3">
                  <div>
                    <Label className="block">Featured on homepage</Label>
                    <p className="text-xs text-muted-foreground">Pin this product to the homepage "Featured" section.</p>
                  </div>
                  <Switch checked={!!form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} data-testid="featured-toggle" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Fire classes (comma separated: A, B, C, E)</Label>
                  <Input value={(form.fireClasses || []).join(', ')} onChange={(e) => setForm({ ...form, fireClasses: e.target.value.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) })} className="h-11" placeholder="A, B, C" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Additional images (gallery)</Label>
                  <p className="text-xs text-muted-foreground mb-2">Optional. Shown as thumbnails on the product page; each can be changed or removed on its own. The primary image above always appears first.</p>
                  <ProductImagesEditor images={form.images} onChange={(v) => setForm({ ...form, images: v })} />
                </div>
                <div>
                  <Label className="mb-1.5 block">Variants / SKUs & specifications</Label>
                  <VariantEditor variants={form.variants} onChange={(v) => setForm({ ...form, variants: v })} />
                </div>
              </>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" data-testid="cms-save-btn" disabled={saving}>{saving ? 'Saving…' : `Save ${singular}`}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={toDelete !== null} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {singular.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const TYPE_COLORS = { contact: 'default', enquiry: 'default', partner: 'secondary', career: 'secondary', newsletter: 'outline' };

function Submissions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    api.get('/submissions').then((r) => setItems(asArray(r.data))).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try { await api.delete(`/submissions/${id}`); load(); } catch (e) { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const filtered = useMemo(() => items.filter((s) => JSON.stringify(s).toLowerCase().includes(q.toLowerCase())), [items, q]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="font-display font-extrabold text-3xl text-white">Submissions</h2>
          <p className="text-muted-foreground">{items.length} messages received</p>
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search submissions" className="h-11 pl-10 w-64" />
        </div>
      </div>
      {loading ? <p className="text-muted-foreground text-center py-12">Loading…</p> : (
        <div className="space-y-4">
          {filtered.map((s) => (
            <div key={s.id} className="card-dark rounded-2xl p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Badge variant={TYPE_COLORS[s.type] || 'secondary'}>{s.type}</Badge>
                  {s.name && <span className="font-heading font-bold text-white">{s.name}</span>}
                  {s.company && <span className="text-muted-foreground flex items-center gap-1.5 text-sm"><Building2 className="w-3.5 h-3.5" /> {s.company}</span>}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground">{new Date(s.createdAt).toLocaleString()}</span>
                  <button onClick={() => del(s.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-muted-foreground">
                {s.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {s.email}</span>}
                {s.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {s.phone}</span>}
                {s.city && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {s.city}</span>}
                {s.jobTitle && <span className="flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> {s.jobTitle}</span>}
                {s.partnerType && <span className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {s.partnerType}</span>}
                {s.resume && <span className="flex items-center gap-1.5">📎 {s.resume}</span>}
              </div>
              {s.message && <p className="text-white mt-3">{s.message}</p>}
              {s.items?.length > 0 && (
                <div className="mt-3 rounded-xl bg-secondary/60 p-3">
                  <div className="text-xs font-semibold text-primary uppercase mb-2">Enquiry items ({s.items.length})</div>
                  {s.items.map((it, idx) => (
                    <div key={idx} className="flex justify-between text-sm text-gray-300 py-0.5">
                      <span>{it.name}{it.sku ? ` — ${it.sku}` : ''}</span><span>Qty: {it.qty}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && <p className="text-muted-foreground text-center py-12">No submissions found.</p>}
        </div>
      )}
    </div>
  );
}

function HeroSettings() {
  const [form, setForm] = useState({ heroVideoUrl: '', heroPosterUrl: '', logoUrl: '', employeeMailUrl: '', productRangeImages: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { refresh } = useSite();

  useEffect(() => {
    api.get('/settings').then((r) => setForm({
      heroVideoUrl: r.data.heroVideoUrl || '', heroPosterUrl: r.data.heroPosterUrl || '',
      logoUrl: r.data.logoUrl || '', employeeMailUrl: r.data.employeeMailUrl || '',
      productRangeImages: (r.data.productRangeImages && typeof r.data.productRangeImages === 'object' && !Array.isArray(r.data.productRangeImages)) ? r.data.productRangeImages : {},
    })).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/settings', form);
      refresh();
      toast({ title: 'Settings saved', description: 'Site settings have been updated.' });
    } catch (err) {
      toast({ title: 'Save failed', description: apiErrorMessage(err), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-muted-foreground py-12 text-center">Loading…</p>;

  return (
    <div className="max-w-3xl">
      <h2 className="font-display font-extrabold text-3xl text-white mb-2">Hero & Settings</h2>
      <p className="text-muted-foreground mb-6">Swap the homepage hero, replace the site logo, and set the employee webmail link — no code required.</p>
      <form onSubmit={save} className="card-dark rounded-2xl p-6 space-y-5">
        <div>
          <Label className="mb-1.5 block">Site logo (header & footer)</Label>
          <div className="max-w-xs"><ImageUpload value={form.logoUrl} onChange={(v) => setForm({ ...form, logoUrl: v })} /></div>
          <p className="text-xs text-muted-foreground mt-1">Leave empty to use the default Closefire logo.</p>
        </div>
        <div>
          <Label className="mb-1.5 flex items-center gap-2"><LinkIcon className="w-4 h-4 text-primary" /> Employee webmail login URL</Label>
          <Input data-testid="employee-mail-url" value={form.employeeMailUrl} onChange={(e) => setForm({ ...form, employeeMailUrl: e.target.value })} placeholder="https://mail.google.com" className="h-11" />
          <p className="text-xs text-muted-foreground mt-1">The "Employee Login" button opens this URL in a new tab.</p>
        </div>
        <div>
          <Label className="mb-1.5 flex items-center gap-2"><Film className="w-4 h-4 text-primary" /> Hero video URL (.mp4)</Label>
          <Input data-testid="hero-video-url" value={form.heroVideoUrl} onChange={(e) => setForm({ ...form, heroVideoUrl: e.target.value })} placeholder="https://…/video.mp4" className="h-11" />
          {form.heroVideoUrl && (
            <video key={form.heroVideoUrl} src={form.heroVideoUrl} className="mt-3 w-full max-h-56 rounded-xl object-cover bg-black" autoPlay muted loop playsInline />
          )}
        </div>
        <div>
          <Label className="mb-1.5 block">Hero poster / fallback image URL</Label>
          <Input data-testid="hero-poster-url" value={form.heroPosterUrl} onChange={(e) => setForm({ ...form, heroPosterUrl: e.target.value })} placeholder="https://…/image.jpg" className="h-11" />
          {form.heroPosterUrl && <img src={form.heroPosterUrl} alt="poster" className="mt-3 w-full max-h-56 rounded-xl object-cover" />}
        </div>

        <div className="pt-2 border-t border-border" data-testid="homepage-product-range-images">
          <Label className="mb-1 block">Homepage → Certified Fire Safety Equipment images</Label>
          <p className="text-xs text-muted-foreground mb-4">The four cards in the homepage “Certified Fire Safety Equipment” section. Upload to replace a card’s image; leave a card empty to keep its built-in default image.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {PRODUCT_RANGE.map((card) => (
              <div key={card.title} data-testid={`product-range-image-${card.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <Label className="mb-1.5 block text-sm text-gray-300">{card.title}</Label>
                <ImageUpload
                  value={form.productRangeImages?.[card.title] || ''}
                  onChange={(v) => setForm((f) => ({ ...f, productRangeImages: { ...f.productRangeImages, [card.title]: v } }))}
                />
              </div>
            ))}
          </div>
        </div>

        <Button type="submit" data-testid="save-hero-btn" disabled={saving} className="gap-2"><Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save settings'}</Button>
      </form>
    </div>
  );
}

function Overview({ setTab }) {
  const [counts, setCounts] = useState({ products: 0, solutions: 0, stories: 0, submissions: 0 });
  useEffect(() => {
    Promise.all([
      api.get('/products').then((r) => asArray(r.data).length).catch(() => 0),
      api.get('/solutions').then((r) => asArray(r.data).length).catch(() => 0),
      api.get('/stories').then((r) => asArray(r.data).length).catch(() => 0),
      api.get('/submissions').then((r) => asArray(r.data).length).catch(() => 0),
    ]).then(([products, solutions, stories, submissions]) => setCounts({ products, solutions, stories, submissions }));
  }, []);
  const stats = [
    { key: 'products', label: 'Products', icon: Package, count: counts.products },
    { key: 'solutions', label: 'Solutions', icon: Layers, count: counts.solutions },
    { key: 'stories', label: 'Success Stories', icon: Trophy, count: counts.stories },
    { key: 'submissions', label: 'Submissions', icon: Inbox, count: counts.submissions },
  ];
  return (
    <div>
      <h2 className="font-display font-extrabold text-3xl text-white mb-6">Dashboard Overview</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {stats.map((s) => (
          <button key={s.key} onClick={() => setTab(s.key)} className="text-left card-dark rounded-2xl p-6 card-hover">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-4"><s.icon className="w-6 h-6" /></span>
            <div className="font-display font-extrabold text-4xl text-white">{s.count}</div>
            <div className="text-muted-foreground font-heading font-semibold mt-1">{s.label}</div>
          </button>
        ))}
      </div>
      <div className="mt-8 card-dark rounded-2xl p-8">
        <h3 className="font-heading font-bold text-xl text-white">Welcome to the Closefire CMS</h3>
        <p className="text-muted-foreground mt-2 max-w-2xl">Manage your products, solutions and success stories, review enquiries and contact submissions, and swap the homepage hero video. Changes reflect instantly on the public website.</p>
        <Button asChild variant="outline" className="mt-5 gap-2"><Link to="/"><ArrowLeft className="w-4 h-4" /> View Public Site</Link></Button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [tab, setTab] = useState('overview');
  const { user, logout } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();

  const doLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen flex bg-secondary/40">
      <aside className="w-64 shrink-0 bg-[#0f1219] text-gray-300 flex flex-col fixed h-screen">
        <Link to="/" className="flex items-center gap-2.5 px-6 h-[72px] border-b border-white/10">
          {settings?.logoUrl
            ? <img src={mediaUrl(settings.logoUrl)} alt="Closefire" className="h-10 w-10 object-contain" />
            : <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary text-white"><Flame className="w-5 h-5" /></span>}
          <span className="font-display text-xl font-extrabold tracking-wide text-white">CLOSE<span className="text-primary">FIRE</span></span>
        </Link>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button key={n.key} onClick={() => setTab(n.key)} data-testid={`dashboard-tab-${n.key}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg font-heading font-semibold text-sm transition-colors ${tab === n.key ? 'bg-primary text-white' : 'hover:bg-white/5 hover:text-white'}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="px-2 mb-3">
            <div className="text-sm font-semibold text-white">{user?.name}</div>
            <div className="text-xs text-gray-500">{user?.email}</div>
          </div>
          <button onClick={doLogout} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg font-heading font-semibold text-sm hover:bg-white/5 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 p-8">
        {tab === 'overview' && <Overview setTab={setTab} />}
        {tab === 'products' && <EntityManager entity="products" title="Products" singular="Product" />}
        {tab === 'solutions' && <EntityManager entity="solutions" title="Solutions" singular="Solution" />}
        {tab === 'stories' && <EntityManager entity="stories" title="Success Stories" singular="Story" />}
        {tab === 'clients' && <EntityManager entity="clients" title="Clients" singular="Client" />}
        {tab === 'certifications' && <EntityManager entity="certifications" title="Certifications" singular="Certification" />}
        {tab === 'submissions' && <Submissions />}
        {tab === 'settings' && <HeroSettings />}
      </main>
    </div>
  );
}
